import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import { cartCount } from "@/lib/cart";
import { getConfig } from "@/lib/config";

const links = [
  { label: "Home", href: "/" },
  { label: "Philosophy", href: "/philosophy" },
  { label: "Collection", href: "/collection" },
  { label: "Festivals", href: "/festivals" },
  { label: "Laxwedeepan Exclusive", href: "/exclusive" },
  { label: "Founder", href: "/founder" },
  { label: "Sacred Essentials", href: "/essentials" },
  { label: "Cart", href: "/cart" },
];

export const Nav = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(cartCount());
  const [checkoutOn, setCheckoutOn] = useState(false);

  useEffect(() => {
    const sync = () => setCount(cartCount());
    window.addEventListener("lw-cart", sync);
    getConfig().then((c) => setCheckoutOn(!!c.checkout_enabled));
    return () => window.removeEventListener("lw-cart", sync);
  }, []);

  const visibleLinks = checkoutOn ? links : links.filter((l) => l.href !== "/cart");

  const linkClass = (href) =>
    `text-[9px] font-medium uppercase tracking-[0.15em] transition-colors duration-300 hover:text-brand-maroon ${pathname === href ? "text-brand-maroon" : "text-brand-ink/60"}`;

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 top-0 z-50 border-b border-brand-border/70 bg-brand-ivory/85 backdrop-blur-md"
      data-testid="site-nav"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
        <Link to="/" data-testid="nav-brand-link" className="flex items-center gap-2.5" onClick={() => setOpen(false)}>
          <img src="/assets/nandi.png" alt="Nandi — the sacred cow of Laxwedeepan" className="h-9 w-auto" />
          <span className="font-display text-2xl tracking-wide text-brand-ink">laxwedeepan</span>
        </Link>
        <nav className="hidden items-center gap-4 lg:flex">
          {visibleLinks.map((l) => (
            <Link key={l.label} to={l.href} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className={linkClass(l.href)}>
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-4">
          {checkoutOn && (
            <Link to="/cart" data-testid="nav-cart-link" aria-label="Open cart" className="relative flex h-10 w-10 items-center justify-center rounded-full border border-brand-border text-brand-ink transition-colors duration-300 hover:border-brand-gold hover:text-brand-maroon">
              <ShoppingBag className="h-4 w-4" strokeWidth={1.5} />
              {count > 0 && (
                <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-brand-maroon text-[9px] font-medium text-brand-ivory" data-testid="nav-cart-count">
                  {count}
                </span>
              )}
            </Link>
          )}
          <Link
            to="/exclusive"
            data-testid="nav-enquire-button"
            className="hidden rounded-full border border-brand-gold px-6 py-2 text-[11px] font-medium uppercase tracking-[0.25em] text-brand-maroon transition-colors duration-300 hover:bg-brand-gold hover:text-brand-ivory xl:inline-block"
          >
            Enquire
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-brand-border text-brand-ink lg:hidden"
          >
            {open ? <X className="h-4 w-4" strokeWidth={1.5} /> : <Menu className="h-4 w-4" strokeWidth={1.5} />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-brand-border bg-brand-ivory/95 px-6 py-6 backdrop-blur-md lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-5">
            {visibleLinks.map((l) => (
              <Link
                key={l.label}
                to={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={linkClass(l.href)}
              >
                {l.label}
              </Link>
            ))}
          </div>
        </nav>
      )}
    </motion.header>
  );
};
